// Automatic quality check (F-10, D-55): per-frame statistics of A and B, and the suspicious segments derived from
// them for M-02 flicker, M-03 texture boiling, M-04 motion (freeze / jitter), M-09 colour, M-10 banding,
// M-11 clipping and tone, M-13 segment seams. Pure logic (no DOM); the worker feeds it RGBA pixels.
// Everything is a navigation hint (D-07, P-01): single-video properties are measured on A and B alike and only
// B's change relative to A is reported (P-03).

/** Regions per frame: a GRID_X × GRID_Y grid over the compared area. */
export const GRID_X = 6;
export const GRID_Y = 4;
export const CELLS = GRID_X * GRID_Y;

/** Per-source state carried to the next frame (temporal measures). */
export interface SourceState {
  lo: Float32Array;
  loW: number;
  loH: number;
  hf: Float32Array;
  hfW: number;
  hfH: number;
}

/** Statistics of one A frame and the B frame shown with it. Plain data (sent from the worker). */
export interface FrameStats {
  /** Cells with enough (masked) pixels to be measured. */
  valid: Uint8Array;
  /** Mean luma (0–255) per cell. */
  lumaA: Float32Array;
  lumaB: Float32Array;
  /** CIE Lab of each cell's mean colour (L, a, b per cell). */
  labA: Float32Array;
  labB: Float32Array;
  /** Skin (found on A): ΔE and hue-angle change (degrees) of the mean skin colour; null when too little skin. */
  skinDE: number | null;
  skinHue: number | null;
  /** Mean saturation (max − min of R, G, B). */
  satA: number;
  satB: number;
  /** Fraction of pixels clipped high (a channel ≥ 254) / low (all channels ≤ 2), whole area. */
  hiA: number;
  hiB: number;
  loA: number;
  loB: number;
  /** Per cell: fraction of pixels newly clipped in B (clipped in B, not in A). */
  newClip: Float32Array;
  /** Luma histogram distance (earth mover's, fraction of the full range). */
  tone: number;
  /** Per cell banding density (step-next-to-plateau pixels / smooth pixels); NaN = no smooth area. */
  bandA: Float32Array;
  bandB: Float32Array;
  /** Mean absolute luma difference A vs B. */
  diff: number;
  /** Mean motion (analysis pixels) since the previous frame; null for the first frame or a repeated B frame. */
  /** Mean absolute change of the (blurred, low-resolution) picture since the previous frame; null like motion. */
  changeA: number | null;
  changeB: number | null;
  motionA: number | null;
  motionB: number | null;
  /**
   * Per cell texture change after motion compensation, two values per cell: RMS of the change (code values) and the
   * cell's large-scale contrast (standard deviation of the blurred picture); NaN = not measured.
   */
  boilA: Float32Array | null;
  boilB: Float32Array | null;
  /** Face check of the main face (F-10.11), added by the face worker; centre in A pixels. */
  face?: (import('./face').FaceStat & { at: { x: number; y: number }; cell: number | null }) | null;
}

// ------------------------------------------------------------------------------------------------ colour helpers

function srgbToLinear(c: number): number {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

function labF(t: number): number {
  return t > 216 / 24389 ? Math.cbrt(t) : (24389 / 27 * t + 16) / 116;
}

/** CIE Lab (D65) of an sRGB colour given as 0–255 code values. */
export function rgbToLab(r: number, g: number, b: number): [number, number, number] {
  const R = srgbToLinear(r);
  const G = srgbToLinear(g);
  const B = srgbToLinear(b);
  const x = (0.4124 * R + 0.3576 * G + 0.1805 * B) / 0.95047;
  const y = 0.2126 * R + 0.7152 * G + 0.0722 * B;
  const z = (0.0193 * R + 0.1192 * G + 0.9505 * B) / 1.08883;
  const fx = labF(x);
  const fy = labF(y);
  const fz = labF(z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

export function deltaE(l1: number, a1: number, b1: number, l2: number, a2: number, b2: number): number {
  return Math.hypot(l1 - l2, a1 - a2, b1 - b2);
}

function hueDiffDeg(a1: number, b1: number, a2: number, b2: number): number {
  let d = (Math.atan2(b2, a2) - Math.atan2(b1, a1)) * (180 / Math.PI);
  while (d > 180) d -= 360;
  while (d < -180) d += 360;
  return Math.abs(d);
}

// ------------------------------------------------------------------------------------------------ per-source measures

export function lumaOf(rgba: ArrayLike<number>, n: number): Float32Array {
  const y = new Float32Array(n);
  for (let i = 0; i < n; i++) y[i] = 0.2126 * rgba[i * 4] + 0.7152 * rgba[i * 4 + 1] + 0.0722 * rgba[i * 4 + 2];
  return y;
}

function cellOf(x: number, y: number, w: number, h: number): number {
  return Math.min(GRID_Y - 1, Math.floor((y * GRID_Y) / h)) * GRID_X + Math.min(GRID_X - 1, Math.floor((x * GRID_X) / w));
}

/** Box-downsampled copy (factor f). */
export function downsample(src: Float32Array, w: number, h: number, f: number): { data: Float32Array; w: number; h: number } {
  const ow = Math.max(1, Math.floor(w / f));
  const oh = Math.max(1, Math.floor(h / f));
  const out = new Float32Array(ow * oh);
  const inv = 1 / (f * f);
  for (let y = 0; y < oh; y++)
    for (let x = 0; x < ow; x++) {
      let s = 0;
      for (let dy = 0; dy < f; dy++) {
        const row = (y * f + dy) * w + x * f;
        for (let dx = 0; dx < f; dx++) s += src[row + dx];
      }
      out[y * ow + x] = s * inv;
    }
  return { data: out, w: ow, h: oh };
}

/** High-frequency layer: image minus its 3×3 box blur. */
export function highPass(src: Float32Array, w: number, h: number): Float32Array {
  const out = new Float32Array(w * h);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      let s = 0;
      let n = 0;
      for (let dy = -1; dy <= 1; dy++) {
        const yy = y + dy;
        if (yy < 0 || yy >= h) continue;
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx;
          if (xx < 0 || xx >= w) continue;
          s += src[yy * w + xx];
          n++;
        }
      }
      out[y * w + x] = src[y * w + x] - s / n;
    }
  return out;
}

const BLOCK = 8;
const SEARCH = 3;

/**
 * Block matching (8×8 blocks, ±3 px) of cur against prev on low-resolution luma, with the block means removed so a
 * brightness change is not taken for motion. A vector other than 0 must match clearly better than staying put (noise
 * alone does not move). Returns each block's vector (NaN for blocks without texture) and the mean motion magnitude.
 */
export function blockMotion(prev: Float32Array, cur: Float32Array, w: number, h: number): { vx: Float32Array; vy: Float32Array; bw: number; bh: number; mean: number | null } {
  const bw = Math.floor(w / BLOCK);
  const bh = Math.floor(h / BLOCK);
  const vx = new Float32Array(bw * bh).fill(NaN);
  const vy = new Float32Array(bw * bh).fill(NaN);
  let sum = 0;
  let n = 0;
  for (let by = 0; by < bh; by++)
    for (let bx = 0; bx < bw; bx++) {
      const x0 = bx * BLOCK;
      const y0 = by * BLOCK;
      let mn = Infinity;
      let mx = -Infinity;
      for (let y = 0; y < BLOCK; y++)
        for (let x = 0; x < BLOCK; x++) {
          const v = cur[(y0 + y) * w + x0 + x];
          if (v < mn) mn = v;
          if (v > mx) mx = v;
        }
      if (mx - mn < 10) continue;
      let mc = 0;
      for (let y = 0; y < BLOCK; y++) for (let x = 0; x < BLOCK; x++) mc += cur[(y0 + y) * w + x0 + x];
      mc /= BLOCK * BLOCK;
      let best = Infinity;
      let bdx = 0;
      let bdy = 0;
      let zero = Infinity;
      for (let dy = -SEARCH; dy <= SEARCH; dy++) {
        if (y0 + dy < 0 || y0 + dy + BLOCK > h) continue;
        for (let dx = -SEARCH; dx <= SEARCH; dx++) {
          if (x0 + dx < 0 || x0 + dx + BLOCK > w) continue;
          let mp = 0;
          for (let y = 0; y < BLOCK; y++) for (let x = 0; x < BLOCK; x++) mp += prev[(y0 + y + dy) * w + x0 + dx + x];
          const off = mc - mp / (BLOCK * BLOCK);
          let s = 0;
          for (let y = 0; y < BLOCK; y++) {
            const rc = (y0 + y) * w + x0;
            const rp = (y0 + y + dy) * w + x0 + dx;
            for (let x = 0; x < BLOCK; x++) s += Math.abs(cur[rc + x] - prev[rp + x] - off);
          }
          if (dx === 0 && dy === 0) zero = s;
          if (s < best - 1e-3 || (Math.abs(s - best) <= 1e-3 && dx * dx + dy * dy < bdx * bdx + bdy * bdy)) {
            best = s;
            bdx = dx;
            bdy = dy;
          }
        }
      }
      if ((bdx || bdy) && best > zero * 0.7) {
        bdx = 0;
        bdy = 0;
      }
      vx[by * bw + bx] = bdx;
      vy[by * bw + bx] = bdy;
      sum += Math.hypot(bdx, bdy);
      n++;
    }
  return { vx, vy, bw, bh, mean: n >= 4 ? sum / n : null };
}

/**
 * Texture change per cell: RMS (code values) of the high-frequency layer's change after motion compensation. Grain
 * that is new every frame and imperfect motion compensation count too, so only B's excess over A is reported.
 */
export function boilCells(
  hf: Float32Array,
  prevHf: Float32Array,
  w: number,
  h: number,
  mv: { vx: Float32Array; vy: Float32Array; bw: number; bh: number },
  mvScale: number,
  weight: Uint8Array | null,
  weightW: number,
  weightScale: number,
): Float32Array {
  const res = new Float64Array(CELLS);
  const cnt = new Float64Array(CELLS);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      if (weight && !weight[Math.floor(y * weightScale) * weightW + Math.floor(x * weightScale)]) continue;
      const bx = Math.min(mv.bw - 1, Math.floor(x / (BLOCK * mvScale)));
      const by = Math.min(mv.bh - 1, Math.floor(y / (BLOCK * mvScale)));
      const k = by * mv.bw + bx;
      const vx = Number.isNaN(mv.vx[k]) ? 0 : Math.round(mv.vx[k] * mvScale);
      const vy = Number.isNaN(mv.vy[k]) ? 0 : Math.round(mv.vy[k] * mvScale);
      const px = x + vx;
      const py = y + vy;
      if (px < 0 || py < 0 || px >= w || py >= h) continue;
      const c = hf[y * w + x];
      const p = prevHf[py * w + px];
      const cell = cellOf(x, y, w, h);
      res[cell] += (c - p) ** 2;
      cnt[cell]++;
    }
  const out = new Float32Array(CELLS).fill(NaN);
  for (let i = 0; i < CELLS; i++) if (cnt[i] > 50) out[i] = Math.sqrt(res[i] / cnt[i]);
  return out;
}

/**
 * Banding per cell: in smooth areas (low mean gradient), the density of pixels where the level steps by ≥ 2 code
 * values between two flat plateaus. Grain and gentle 1-code ramps score ~0; posterised gradients score high.
 */
export function bandCells(y: Float32Array, w: number, h: number, weight: Uint8Array | null): Float32Array {
  const steps = new Float64Array(CELLS);
  const smooth = new Float64Array(CELLS);
  const total = new Float64Array(CELLS);
  const B = 8;
  const flat = (a: number, b: number) => Math.abs(a - b) < 0.3;
  for (let by = 0; by + B <= h; by += B)
    for (let bx = 0; bx + B <= w; bx += B) {
      let g = 0;
      let mn = Infinity;
      let mx = -Infinity;
      let masked = 0;
      for (let yy = by; yy < by + B; yy++)
        for (let xx = bx; xx < bx + B; xx++) {
          const i = yy * w + xx;
          if (weight && !weight[i]) masked++;
          const v = y[i];
          if (v < mn) mn = v;
          if (v > mx) mx = v;
          if (xx + 1 < w) g += Math.abs(y[i + 1] - v);
          if (yy + 1 < h) g += Math.abs(y[i + w] - v);
        }
      const cell = cellOf(bx + B / 2, by + B / 2, w, h);
      total[cell] += B * B;
      if (masked > (B * B) / 2) continue;
      if (g / (2 * B * B) > 2.5 || mx - mn > 40) continue;
      smooth[cell] += B * B;
      for (let yy = by; yy < by + B; yy++)
        for (let xx = bx; xx < bx + B; xx++) {
          const i = yy * w + xx;
          let hit = false;
          if (xx >= 2 && xx + 3 < w) {
            const d = Math.abs(y[i + 1] - y[i]);
            if (d >= 2 && flat(y[i - 1], y[i]) && flat(y[i - 2], y[i - 1]) && flat(y[i + 2], y[i + 1]) && flat(y[i + 3], y[i + 2])) hit = true;
          }
          if (!hit && yy >= 2 && yy + 3 < h) {
            const d = Math.abs(y[i + w] - y[i]);
            if (d >= 2 && flat(y[i - w], y[i]) && flat(y[i - 2 * w], y[i - w]) && flat(y[i + 2 * w], y[i + w]) && flat(y[i + 3 * w], y[i + 2 * w])) hit = true;
          }
          if (hit) steps[cell]++;
        }
    }
  const out = new Float32Array(CELLS);
  for (let i = 0; i < CELLS; i++) out[i] = total[i] && smooth[i] >= total[i] * 0.03 ? steps[i] / smooth[i] : NaN;
  return out;
}

// ------------------------------------------------------------------------------------------------ one frame pair

export interface PairInput {
  a: ArrayLike<number>;
  b: ArrayLike<number>;
  w: number;
  h: number;
  /** 1 = measure this pixel (mask of the protected region); null = everything. */
  weight: Uint8Array | null;
  prevA: SourceState | null;
  /** Null for the first frame; pass the previous state with sameB when B shows the same frame again. */
  prevB: SourceState | null;
  sameB: boolean;
}

function sourceState(y: Float32Array, w: number, h: number): SourceState {
  // Motion works on a blurred low-resolution copy (grain and noise must not look like motion).
  const fLo = Math.max(2, Math.round(w / 320));
  const fHf = Math.max(1, Math.round(w / 480));
  const lo = downsample(y, w, h, fLo);
  const hp = highPass(lo.data, lo.w, lo.h);
  const blurred = lo.data.map((v, i) => v - hp[i]);
  const mid = downsample(y, w, h, fHf);
  return { lo: blurred, loW: lo.w, loH: lo.h, hf: highPass(mid.data, mid.w, mid.h), hfW: mid.w, hfH: mid.h };
}

function temporal(prev: SourceState | null, cur: SourceState, w: number, weight: Uint8Array | null): { motion: number | null; change: number | null; boil: Float32Array | null } {
  if (!prev || prev.loW !== cur.loW || prev.hfW !== cur.hfW) return { motion: null, change: null, boil: null };
  const mv = blockMotion(prev.lo, cur.lo, cur.loW, cur.loH);
  const loScale = w / cur.loW;
  const mvScale = cur.hfW / cur.loW;
  const rms = boilCells(cur.hf, prev.hf, cur.hfW, cur.hfH, mv, mvScale, weight, w, w / cur.hfW);
  // Large-scale contrast per cell: a contrast change scales the texture change too and must not count as boiling.
  const m = new Float64Array(CELLS);
  const m2 = new Float64Array(CELLS);
  const k = new Float64Array(CELLS);
  for (let y = 0; y < cur.loH; y++)
    for (let x = 0; x < cur.loW; x++) {
      const c = cellOf(x, y, cur.loW, cur.loH);
      const v = cur.lo[y * cur.loW + x];
      m[c] += v;
      m2[c] += v * v;
      k[c]++;
    }
  const boil = new Float32Array(CELLS * 2);
  for (let c = 0; c < CELLS; c++) {
    boil[c * 2] = rms[c];
    boil[c * 2 + 1] = k[c] ? Math.sqrt(Math.max(0, m2[c] / k[c] - (m[c] / k[c]) ** 2)) : NaN;
  }
  let change = 0;
  for (let i = 0; i < cur.lo.length; i++) change += Math.abs(cur.lo[i] - prev.lo[i]);
  return { motion: mv.mean === null ? null : mv.mean * loScale, change: change / cur.lo.length, boil };
}

export function measurePair(p: PairInput): { stats: FrameStats; stateA: SourceState; stateB: SourceState } {
  const { a, b, w, h, weight } = p;
  const n = w * h;
  const yA = lumaOf(a, n);
  const yB = lumaOf(b, n);
  const cnt = new Float64Array(CELLS);
  const sum = new Float64Array(CELLS * 6);
  const clipNew = new Float64Array(CELLS);
  const histA = new Float64Array(32);
  const histB = new Float64Array(32);
  let skinN = 0;
  const skin = [0, 0, 0, 0, 0, 0];
  let total = 0;
  let satA = 0;
  let satB = 0;
  let hiA = 0;
  let hiB = 0;
  let loA = 0;
  let loB = 0;
  let diff = 0;
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (weight && !weight[i]) continue;
      const k = i * 4;
      const ra = a[k];
      const ga = a[k + 1];
      const ba = a[k + 2];
      const rb = b[k];
      const gb = b[k + 1];
      const bb = b[k + 2];
      const c = cellOf(x, y, w, h);
      cnt[c]++;
      const s = c * 6;
      sum[s] += ra;
      sum[s + 1] += ga;
      sum[s + 2] += ba;
      sum[s + 3] += rb;
      sum[s + 4] += gb;
      sum[s + 5] += bb;
      total++;
      const maxA = Math.max(ra, ga, ba);
      const maxB = Math.max(rb, gb, bb);
      satA += maxA - Math.min(ra, ga, ba);
      satB += maxB - Math.min(rb, gb, bb);
      const hA = maxA >= 254;
      const hB = maxB >= 254;
      const lA = maxA <= 2;
      const lB = maxB <= 2;
      if (hA) hiA++;
      if (hB) hiB++;
      if (lA) loA++;
      if (lB) loB++;
      if ((hB && !hA) || (lB && !lA)) clipNew[c]++;
      histA[Math.min(31, yA[i] >> 3)]++;
      histB[Math.min(31, yB[i] >> 3)]++;
      diff += Math.abs(yA[i] - yB[i]);
      // Skin on A (YCbCr rule); B is measured at the same pixels.
      const cb = 128 - 0.1687 * ra - 0.3313 * ga + 0.5 * ba;
      const cr = 128 + 0.5 * ra - 0.4187 * ga - 0.0813 * ba;
      if (yA[i] > 50 && cb >= 77 && cb <= 127 && cr >= 133 && cr <= 173) {
        skinN++;
        skin[0] += ra;
        skin[1] += ga;
        skin[2] += ba;
        skin[3] += rb;
        skin[4] += gb;
        skin[5] += bb;
      }
    }
  const valid = new Uint8Array(CELLS);
  const lumaA = new Float32Array(CELLS);
  const lumaB = new Float32Array(CELLS);
  const labA = new Float32Array(CELLS * 3);
  const labB = new Float32Array(CELLS * 3);
  const newClip = new Float32Array(CELLS);
  const cellPx = n / CELLS;
  for (let c = 0; c < CELLS; c++) {
    if (cnt[c] < Math.max(16, cellPx * 0.1)) continue;
    valid[c] = 1;
    const s = c * 6;
    const m = sum.slice(s, s + 6).map((v) => v / cnt[c]);
    lumaA[c] = 0.2126 * m[0] + 0.7152 * m[1] + 0.0722 * m[2];
    lumaB[c] = 0.2126 * m[3] + 0.7152 * m[4] + 0.0722 * m[5];
    labA.set(rgbToLab(m[0], m[1], m[2]), c * 3);
    labB.set(rgbToLab(m[3], m[4], m[5]), c * 3);
    newClip[c] = clipNew[c] / cnt[c];
  }
  let skinDE: number | null = null;
  let skinHue: number | null = null;
  if (total && skinN >= total * 0.005 && skinN >= 20) {
    const la = rgbToLab(skin[0] / skinN, skin[1] / skinN, skin[2] / skinN);
    const lb = rgbToLab(skin[3] / skinN, skin[4] / skinN, skin[5] / skinN);
    skinDE = deltaE(la[0], la[1], la[2], lb[0], lb[1], lb[2]);
    skinHue = Math.hypot(la[1], la[2]) >= 8 ? hueDiffDeg(la[1], la[2], lb[1], lb[2]) : null;
  }
  let tone = 0;
  let ca = 0;
  let cb = 0;
  for (let i = 0; i < 32; i++) {
    ca += histA[i] / (total || 1);
    cb += histB[i] / (total || 1);
    tone += Math.abs(ca - cb);
  }
  const stateA = sourceState(yA, w, h);
  const stateB = p.sameB && p.prevB ? p.prevB : sourceState(yB, w, h);
  const tA = temporal(p.prevA, stateA, w, weight);
  const tB = p.sameB ? { motion: null, change: null, boil: null } : temporal(p.prevB, stateB, w, weight);
  const t = total || 1;
  const stats: FrameStats = {
    valid,
    lumaA,
    lumaB,
    labA,
    labB,
    skinDE,
    skinHue,
    satA: satA / t,
    satB: satB / t,
    hiA: hiA / t,
    hiB: hiB / t,
    loA: loA / t,
    loB: loB / t,
    newClip,
    tone: tone / 32,
    bandA: bandCells(yA, w, h, weight),
    bandB: bandCells(yB, w, h, weight),
    diff: diff / t,
    changeA: tA.change,
    changeB: tB.change,
    motionA: tA.motion,
    motionB: tB.motion,
    boilA: tA.boil,
    boilB: tB.boil,
  };
  return { stats, stateA, stateB };
}

// ------------------------------------------------------------------------------------------------ findings

export type QualityCat = 'frames' | 'face' | 'seam' | 'color' | 'clip' | 'banding' | 'flicker' | 'boiling' | 'motion' | 'ai';
export const QUALITY_CATS: readonly QualityCat[] = ['ai', 'frames', 'face', 'motion', 'seam', 'flicker', 'color', 'clip', 'banding', 'boiling'];
export type Sensitivity = 'low' | 'medium' | 'high';
/** Thresholds are divided by this (owner, 2026-10-01): low = thresholds × 2, high = thresholds × 0.5. */
export const SENSITIVITY: Record<Sensitivity, number> = { low: 0.5, medium: 1, high: 2 };

/** Default thresholds (medium sensitivity); a frame's score is value / threshold, suspicious at ≥ 1. */
export const THRESHOLDS = {
  faceDrift: 0.06,
  faceBlink: 0.45,
  faceMouth: 0.35,
  faceTurn: 0.32,
  skinDE: 5,
  skinHue: 6,
  cellDE: 10,
  satLoss: 0.25,
  newClipAll: 0.004,
  newClipCell: 0.03,
  tone: 0.06,
  band: 0.02,
  flickerLuma: 3,
  flickerChroma: 2,
  boilDiff: 2.5,
  boilRatio: 1.3,
  motionMin: 0.3,
  freezeRatio: 0.35,
  jitterRatio: 2.5,
  seamDE: 1.5,
  seamLuma: 2.5,
};

export interface Finding {
  cat: QualityCat;
  /** A frames, inclusive. */
  start: number;
  end: number;
  /** Most suspicious A frame. */
  peak: number;
  score: number;
  /** Cell (0…CELLS−1) where it is strongest, or null for whole-frame measures. */
  cell: number | null;
  /** Sub-type for the label (freeze / jitter, skin / cell / sat, high / low / tone, drop / insert / duplicate). */
  kind: string;
  /** Measured value at the peak for the label. */
  value: number;
  /** Exact place in A pixels (faces). */
  at?: { x: number; y: number };
  /** For frame events (drop / insert / duplicate): count and B frame. */
  count?: number;
  bFrame?: number;
  /** AI look (F-10.10): what the model said and where (A pixels). */
  text?: string;
  box?: { x: number; y: number; w: number; h: number } | null;
}

export interface FrameEvent {
  kind: 'drop' | 'insert' | 'duplicate';
  aFrame: number;
  bFrame: number;
  count: number;
}

/** One category's measure at one A frame: score (≥ 1 = suspicious at medium sensitivity) and where / what. */
export interface Point {
  score: number;
  cell: number | null;
  kind: string;
  value: number;
  /** Exact place in A pixels (faces), else the cell centre is used. */
  at?: { x: number; y: number };
}

function argmaxCell(vals: ArrayLike<number>, valid?: Uint8Array): { cell: number; v: number } | null {
  let best: { cell: number; v: number } | null = null;
  for (let c = 0; c < vals.length; c++) {
    const v = vals[c];
    if (Number.isNaN(v) || (valid && !valid[c])) continue;
    if (!best || v > best.v) best = { cell: c, v };
  }
  return best;
}

function colorPoint(s: FrameStats): Point | null {
  const T = THRESHOLDS;
  const cands: Point[] = [];
  if (s.skinDE !== null) cands.push({ score: s.skinDE / T.skinDE, cell: null, kind: 'skin', value: s.skinDE });
  if (s.skinHue !== null) cands.push({ score: s.skinHue / T.skinHue, cell: null, kind: 'skinHue', value: s.skinHue });
  const de = new Float32Array(CELLS).fill(NaN);
  for (let c = 0; c < CELLS; c++)
    if (s.valid[c]) de[c] = deltaE(s.labA[c * 3], s.labA[c * 3 + 1], s.labA[c * 3 + 2], s.labB[c * 3], s.labB[c * 3 + 1], s.labB[c * 3 + 2]);
  const m = argmaxCell(de);
  if (m) cands.push({ score: m.v / T.cellDE, cell: m.cell, kind: 'cell', value: m.v });
  if (s.satA >= 15) {
    const loss = 1 - s.satB / s.satA;
    cands.push({ score: loss / T.satLoss, cell: null, kind: 'sat', value: loss });
  }
  return cands.length ? cands.reduce((x, y) => (y.score > x.score ? y : x)) : null;
}

function clipPoint(s: FrameStats): Point {
  const T = THRESHOLDS;
  const cands: Point[] = [
    { score: (s.hiB - s.hiA) / T.newClipAll, cell: null, kind: 'high', value: s.hiB - s.hiA },
    { score: (s.loB - s.loA) / T.newClipAll, cell: null, kind: 'low', value: s.loB - s.loA },
    { score: s.tone / T.tone, cell: null, kind: 'tone', value: s.tone },
  ];
  const m = argmaxCell(s.newClip, s.valid);
  if (m) cands.push({ score: m.v / T.newClipCell, cell: m.cell, kind: s.hiB - s.hiA >= s.loB - s.loA ? 'high' : 'low', value: m.v });
  return cands.reduce((x, y) => (y.score > x.score ? y : x));
}

function bandPoint(s: FrameStats): Point | null {
  const d = new Float32Array(CELLS).fill(NaN);
  for (let c = 0; c < CELLS; c++) {
    if (Number.isNaN(s.bandB[c])) continue;
    d[c] = s.bandB[c] - (Number.isNaN(s.bandA[c]) ? 0 : s.bandA[c]);
  }
  const m = argmaxCell(d);
  return m ? { score: m.v / THRESHOLDS.band, cell: m.cell, kind: 'band', value: m.v } : null;
}

function boilPoint(s: FrameStats): Point | null {
  if (!s.boilA || !s.boilB) return null;
  const T = THRESHOLDS;
  const d = new Float32Array(CELLS).fill(NaN);
  for (let c = 0; c < CELLS; c++) {
    const a = s.boilA[c * 2];
    const b = s.boilB[c * 2];
    if (Number.isNaN(a) || Number.isNaN(b)) continue;
const la = s.boilA[c * 2 + 1];
    const lb = s.boilB[c * 2 + 1];
    const gain = la >= 2 && lb >= 0 ? Math.min(3, Math.max(0.5, lb / la)) : 1;
    const bn = b / gain;
    // Excess in quadrature: B's extra change on top of what A already has (grain, motion-compensation error).
    const excess = Math.sqrt(Math.max(0, bn * bn - a * a));
    d[c] = Math.min(excess / T.boilDiff, bn / (T.boilRatio * Math.max(a, 0.5)));
  }
  const m = argmaxCell(d);
  return m ? { score: m.v, cell: m.cell, kind: 'boil', value: s.boilB[m.cell * 2] - s.boilA[m.cell * 2] } : null;
}

/**
 * Face (F-10.11): B's face missing, features moved (M-05), blink (M-06) or mouth (M-07) different from A. A turned
 * head (M-14) is listed as a place to look closely when nothing else is wrong there.
 */
function facePoint(s: FrameStats): Point | null {
  const f = s.face;
  if (!f) return null;
  const T = THRESHOLDS;
  const at = f.at;
  const cands: Point[] = [
    { score: f.lost ? 2 : 0, cell: f.cell, kind: 'lost', value: 1, at },
    { score: f.drift / T.faceDrift, cell: f.cell, kind: 'drift.' + (f.region ?? 'eyes'), value: f.drift, at },
    { score: f.blink / T.faceBlink, cell: f.cell, kind: 'blink', value: f.blink, at },
    { score: f.mouth / T.faceMouth, cell: f.cell, kind: 'mouth', value: f.mouth, at },
  ];
  const best = cands.reduce((x, y) => (y.score > x.score ? y : x));
  if (best.score < 1 && f.turn >= T.faceTurn) return { score: 1, cell: f.cell, kind: 'turned', value: f.turn, at };
  return best;
}

/** Back-and-forth change at t: the smaller of the changes into and out of t when they go opposite ways, else 0. */
function secondDiff(prev: number, cur: number, next: number): number {
  const d1 = cur - prev;
  const d2 = next - cur;
  return d1 * d2 < 0 ? Math.min(Math.abs(d1), Math.abs(d2)) : 0;
}

function flickerPoint(p: FrameStats, s: FrameStats, n: FrameStats): Point | null {
  const T = THRESHOLDS;
  let best: Point | null = null;
  for (let c = 0; c < CELLS; c++) {
    if (!p.valid[c] || !s.valid[c] || !n.valid[c]) continue;
    const rA = secondDiff(p.lumaA[c], s.lumaA[c], n.lumaA[c]);
    const rB = secondDiff(p.lumaB[c], s.lumaB[c], n.lumaB[c]);
    const cA = Math.hypot(
      secondDiff(p.labA[c * 3 + 1], s.labA[c * 3 + 1], n.labA[c * 3 + 1]),
      secondDiff(p.labA[c * 3 + 2], s.labA[c * 3 + 2], n.labA[c * 3 + 2]),
    );
    const cB = Math.hypot(
      secondDiff(p.labB[c * 3 + 1], s.labB[c * 3 + 1], n.labB[c * 3 + 1]),
      secondDiff(p.labB[c * 3 + 2], s.labB[c * 3 + 2], n.labB[c * 3 + 2]),
    );
    const lum = Math.min((rB - rA) / T.flickerLuma, rB / (1.5 * rA + 0.5));
    const chr = Math.min((cB - cA) / T.flickerChroma, cB / (1.5 * cA + 0.5));
    const pt: Point = lum >= chr ? { score: lum, cell: c, kind: 'luma', value: rB - rA } : { score: chr, cell: c, kind: 'chroma', value: cB - cA };
    if (!best || pt.score > best.score) best = pt;
  }
  return best;
}

function smooth(v: (number | null)[], r: number): (number | null)[] {
  return v.map((_, i) => {
    let s = 0;
    let n = 0;
    for (let k = i - r; k <= i + r; k++) {
      const x = v[k];
      if (x !== null && x !== undefined) {
        s += x;
        n++;
      }
    }
    return n ? s / n : null;
  });
}

function median(v: number[]): number {
  if (!v.length) return 0;
  const s = [...v].sort((a, b) => a - b);
  return s[s.length >> 1];
}

/** Per-frame points per category (index = A frame), before thresholding. */
export type FramePoints = Record<Exclude<QualityCat, 'frames' | 'ai'>, (Point | null)[]>;

export function framePoints(stats: (FrameStats | null)[]): FramePoints {
  const n = stats.length;
  const out = {
    seam: new Array<Point | null>(n).fill(null),
    color: new Array<Point | null>(n).fill(null),
    clip: new Array<Point | null>(n).fill(null),
    banding: new Array<Point | null>(n).fill(null),
    flicker: new Array<Point | null>(n).fill(null),
    boiling: new Array<Point | null>(n).fill(null),
    motion: new Array<Point | null>(n).fill(null),
    face: new Array<Point | null>(n).fill(null),
  };
  const T = THRESHOLDS;
  const de: (number | null)[] = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    const s = stats[i];
    if (!s) continue;
    out.color[i] = colorPoint(s);
    out.clip[i] = clipPoint(s);
    out.banding[i] = bandPoint(s);
    out.boiling[i] = boilPoint(s);
    out.face[i] = facePoint(s);
    const p = stats[i - 1];
    const q = stats[i + 1];
    if (p && q) out.flicker[i] = flickerPoint(p, s, q);
    let sde = 0;
    let k = 0;
    for (let c = 0; c < CELLS; c++)
      if (s.valid[c]) {
        sde += deltaE(s.labA[c * 3], s.labA[c * 3 + 1], s.labA[c * 3 + 2], s.labB[c * 3], s.labB[c * 3 + 1], s.labB[c * 3 + 2]);
        k++;
      }
    de[i] = k ? sde / k : null;
  }
  // M-04: motion of B relative to A over ±2 frames.
  const mA = smooth(stats.map((s) => s?.motionA ?? null), 2);
  const mB = smooth(stats.map((s) => s?.motionB ?? null), 2);
  const cA = smooth(stats.map((s) => s?.changeA ?? null), 2);
  const cB = smooth(stats.map((s) => s?.changeB ?? null), 2);
  for (let i = 0; i < n; i++) {
    const a = mA[i];
    const b = mB[i];
    if (a === null || b === null || !stats[i]) continue;
    // A frozen B barely changes at all; less motion with plenty of change is noise confusing the motion estimate.
    const still = cA[i] === null || cB[i] === null || cB[i]! <= 0.5 * cA[i]!;
    const freeze = a >= T.motionMin && still ? (1 - b / a) / (1 - T.freezeRatio) : 0;
    const base = Math.max(a, T.motionMin * 0.7);
    const jitter = b - a >= 1 ? b / (T.jitterRatio * base) : 0;
    out.motion[i] = freeze >= jitter ? { score: freeze, cell: null, kind: 'freeze', value: a > 0 ? b / a : 0 } : { score: jitter, cell: null, kind: 'jitter', value: b / base };
  }
  // M-13: steps in the A–B difference (colour and structure) that persist, i.e. B's processing changes abruptly.
  const step = (series: (number | null)[], abs: number, kind: string) => {
    const vals = series.filter((x): x is number => x !== null);
    const diffs: number[] = [];
    for (let i = 1; i < series.length; i++) if (series[i] !== null && series[i - 1] !== null) diffs.push(Math.abs(series[i]! - series[i - 1]!));
    const noise = median(diffs) * 1.4826 + 1e-6;
    if (vals.length < 12) return;
    const raw: (Point | null)[] = new Array(n).fill(null);
    for (let i = 5; i < n - 4; i++) {
      const pre: number[] = [];
      const post: number[] = [];
      for (let k = i - 5; k < i; k++) if (series[k] !== null) pre.push(series[k]!);
      for (let k = i; k < i + 5; k++) if (series[k] !== null) post.push(series[k]!);
      if (pre.length < 3 || post.length < 3) continue;
      const d = Math.abs(post.reduce((x, y) => x + y, 0) / post.length - pre.reduce((x, y) => x + y, 0) / pre.length);
      raw[i] = { score: Math.min(d / abs, d / (6 * noise)), cell: null, kind, value: d };
    }
    // Keep only the frame where the step is largest (a step shows as a ±5-frame ramp).
    for (let i = 0; i < n; i++) {
      const r = raw[i];
      if (!r) continue;
      let peak = true;
      for (let k = i - 4; k <= i + 4; k++) if (k !== i && raw[k] && raw[k]!.score > r.score) peak = false;
      if (peak && (!out.seam[i] || out.seam[i]!.score < r.score)) out.seam[i] = r;
    }
  };
  step(de, T.seamDE, 'color');
  step(stats.map((s) => s?.diff ?? null), T.seamLuma, 'luma');
  return out;
}

/**
 * Suspicious segments from per-frame points: runs of frames at or above the threshold (score ≥ 1 / sensitivity),
 * joined across gaps of up to 2 frames. Frame events come from the temporal analysis (F-03.3).
 */
export function findings(stats: (FrameStats | null)[], sensitivity: Sensitivity, events: FrameEvent[] = [], first = 0): { findings: Finding[]; curves: Record<QualityCat, Float32Array> } {
  return segments(framePoints(stats), stats.length, sensitivity, events, first);
}

/** Findings from per-frame points (also used for results restored from a project file). */
export function segments(pts: FramePoints, n: number, sensitivity: Sensitivity, events: FrameEvent[] = [], first = 0): { findings: Finding[]; curves: Record<QualityCat, Float32Array> } {
  const k = SENSITIVITY[sensitivity];
  const res: Finding[] = [];
  const curves = {} as Record<QualityCat, Float32Array>;
  curves.frames = new Float32Array(n).fill(NaN);
  curves.ai = new Float32Array(n).fill(NaN);
  // Repeated frames in a row are one finding.
  const evs = [...events].sort((x, y) => x.aFrame - y.aFrame || x.bFrame - y.bFrame);
  for (const e of evs) {
    const i = e.aFrame - first;
    if (i >= 0 && i < n) curves.frames[i] = 2;
    const prev = res[res.length - 1];
    if (prev && e.kind === 'duplicate' && prev.kind === 'duplicate' && e.aFrame <= prev.end + 1) {
      prev.end = Math.max(prev.end, e.aFrame);
      prev.count = (prev.count ?? 1) + 1;
      prev.value = prev.count;
      continue;
    }
    res.push({ cat: 'frames', start: e.aFrame, end: e.aFrame, peak: e.aFrame, score: 2, cell: null, kind: e.kind, value: e.count, count: e.count, bFrame: e.bFrame });
  }
  // Around frame events and frozen / jittery motion every other measure changes too (B shows other content than
  // A there); the problem is reported once, as the event or the motion finding.
  const covered = new Set<number>();
  for (const f of res) for (let d = f.start - first - 2; d <= f.end - first + 2; d++) covered.add(d);
  pts.motion.forEach((m, i) => {
    if (m && m.score * k >= 1) for (let d = -2; d <= 2; d++) covered.add(i + d);
  });
  for (const cat of Object.keys(pts) as Exclude<QualityCat, 'frames' | 'ai'>[]) {
    const series = pts[cat];
    const curve = new Float32Array(n).fill(NaN);
    series.forEach((p, i) => {
      if (p) curve[i] = p.score * k;
    });
    curves[cat] = curve;
    // Motion and boiling persist; single-frame spikes are compression (keyframe pulsing) or estimate noise.
    const minLen = cat === 'motion' || cat === 'boiling' ? 3 : cat === 'face' ? 2 : 1;
    let i = 0;
    while (i < n) {
      if (!(curve[i] >= 1) || (cat !== 'motion' && covered.has(i))) {
        i++;
        continue;
      }
      let end = i;
      let gap = 0;
      for (let j = i + 1; j < n && gap <= 2; j++) {
        if (curve[j] >= 1 && (cat === 'motion' || !covered.has(j))) {
          end = j;
          gap = 0;
        } else gap++;
      }
      if (end - i + 1 >= minLen) {
        let peak = i;
        for (let j = i; j <= end; j++) if ((curve[j] || 0) > (curve[peak] || 0)) peak = j;
        const p = series[peak]!;
        res.push({ cat, start: i + first, end: end + first, peak: peak + first, score: curve[peak], cell: p.cell, kind: p.kind, value: p.value, at: p.at });
      }
      i = end + 1;
    }
  }
  res.sort((x, y) => x.start - y.start || QUALITY_CATS.indexOf(x.cat) - QUALITY_CATS.indexOf(y.cat));
  return { findings: res, curves };
}

/** Centre of a cell in A pixel coordinates, given the analysed area (A pixels). */
export function cellCenter(cell: number, area: { x: number; y: number; w: number; h: number }): { x: number; y: number } {
  const cx = cell % GRID_X;
  const cy = Math.floor(cell / GRID_X);
  return { x: area.x + ((cx + 0.5) / GRID_X) * area.w, y: area.y + ((cy + 0.5) / GRID_Y) * area.h };
}

// ------------------------------------------------------------------------------------------------ storage

/**
 * Compact form of the per-frame points for the project file: every frame's score (2 decimals, −1 = not measured),
 * and the details (cell, kind, value) only of frames that could be suspicious at the highest sensitivity.
 */
export type PackedPoints = Record<string, { s: number[]; d: Record<string, [number | null, string, number, number?, number?]> }>;

export function packPoints(pts: FramePoints): PackedPoints {
  const minScore = 1 / SENSITIVITY.high - 0.01;
  const out: PackedPoints = {};
  for (const [cat, series] of Object.entries(pts)) {
    const d: Record<string, [number | null, string, number, number?, number?]> = {};
    const s = series.map((p, i) => {
      if (!p) return -1;
      const v = Math.round(Math.max(0, p.score) * 100) / 100;
      if (p.score >= minScore) d[i] = p.at ? [p.cell, p.kind, Math.round(p.value * 1e4) / 1e4, Math.round(p.at.x), Math.round(p.at.y)] : [p.cell, p.kind, Math.round(p.value * 1e4) / 1e4];
      return v;
    });
    out[cat] = { s, d };
  }
  return out;
}

export function unpackPoints(packed: PackedPoints, n: number): FramePoints {
  const out = {} as FramePoints;
  for (const cat of QUALITY_CATS) {
    if (cat === 'frames' || cat === 'ai') continue;
    const p = packed[cat];
    const series: (Point | null)[] = new Array(n).fill(null);
    if (p)
      for (let i = 0; i < n; i++) {
        const v = p.s[i];
        if (v === undefined || v < 0) continue;
        const d = p.d[i];
        series[i] = d ? { score: v, cell: d[0], kind: d[1], value: d[2], ...(d[3] !== undefined && d[4] !== undefined ? { at: { x: d[3], y: d[4] } } : {}) } : { score: v, cell: null, kind: '', value: 0 };
      }
    out[cat] = series;
  }
  return out;
}

/** A finished check as kept in the project (per shot and version). */
export interface StoredQuality {
  /** What was checked (files, offset, alignment, range); results no longer match when it changes. */
  key: string;
  checkedAt: string;
  first: number;
  n: number;
  events: FrameEvent[];
  area: { x: number; y: number; w: number; h: number };
  offsetHint: number | null;
  framesChecked: boolean;
  points: PackedPoints;
  /** AI look results (F-10.10), when it was run. */
  ai?: StoredAiIssue[];
}

export interface StoredAiIssue {
  frame: number;
  type: string;
  severity: number;
  text: string;
  box: { x: number; y: number; w: number; h: number } | null;
}
